import { useImperativeHandle, useRef } from "react";

export interface CompassHandle {
  /** 카메라가 향하는 방위(radians, 0=N, 시계방향 양수). disc를 -azimuth만큼 회전. */
  setAzimuth: (azimuthRad: number) => void;
}

interface CompassProps {
  ref?: React.Ref<CompassHandle>;
  className?: string;
  size?: number;
  /** 클릭 시 정북으로 카메라 리셋 */
  onResetNorth?: () => void;
}

export function Compass({ ref, className, size = 88, onResetNorth }: CompassProps) {
  const discRef = useRef<SVGGElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      setAzimuth(azimuthRad: number) {
        const deg = -(azimuthRad * 180) / Math.PI;
        if (discRef.current) {
          discRef.current.setAttribute("transform", `rotate(${deg.toFixed(2)} 50 50)`);
        }
      },
    }),
    []
  );

  return (
    <button
      type="button"
      onClick={onResetNorth}
      title="정북 방향으로 회전"
      aria-label="정북 방향으로 회전"
      className={
        "group rounded-full bg-black/70 p-2 shadow-lg backdrop-blur-sm transition hover:bg-black/85 hover:ring-2 hover:ring-white/40 active:scale-95 cursor-pointer " +
        (className ?? "")
      }
      style={{ width: size, height: size, border: 0 }}
    >
      <svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden="true">
        {/* 회전하는 디스크 */}
        <g ref={discRef} transform="rotate(0 50 50)">
          {/* 외곽 링 */}
          <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1" />
          {/* 눈금 (NESW + 보조 8개) */}
          {Array.from({ length: 16 }).map((_, i) => {
            const angle = (i / 16) * Math.PI * 2 - Math.PI / 2;
            const r1 = 42;
            const r2 = i % 4 === 0 ? 36 : 39;
            const x1 = 50 + Math.cos(angle) * r1;
            const y1 = 50 + Math.sin(angle) * r1;
            const x2 = 50 + Math.cos(angle) * r2;
            const y2 = 50 + Math.sin(angle) * r2;
            return (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="rgba(255,255,255,0.5)"
                strokeWidth={i % 4 === 0 ? 1.2 : 0.6}
              />
            );
          })}

          {/* 북쪽 화살표 (위쪽으로) */}
          <polygon points="50,8 45,22 55,22" fill="#ef4444" />
          <polygon points="50,92 45,78 55,78" fill="#e2e8f0" />

          {/* 방위 라벨 */}
          <text
            x="50"
            y="20"
            textAnchor="middle"
            fontFamily="ui-monospace, monospace"
            fontSize="10"
            fontWeight="700"
            fill="#ef4444"
          >
            N
          </text>
          <text
            x="80"
            y="54"
            textAnchor="middle"
            fontFamily="ui-monospace, monospace"
            fontSize="9"
            fill="rgba(255,255,255,0.85)"
          >
            E
          </text>
          <text
            x="50"
            y="86"
            textAnchor="middle"
            fontFamily="ui-monospace, monospace"
            fontSize="9"
            fill="rgba(255,255,255,0.85)"
          >
            S
          </text>
          <text
            x="20"
            y="54"
            textAnchor="middle"
            fontFamily="ui-monospace, monospace"
            fontSize="9"
            fill="rgba(255,255,255,0.85)"
          >
            W
          </text>
        </g>

        {/* 중심점 (고정) */}
        <circle cx="50" cy="50" r="2" fill="rgba(255,255,255,0.7)" />
      </svg>
    </button>
  );
}
